import { isEmpty, isNil } from "lodash";
import React, { useMemo } from "react";
import AssociationChipsByIds from "./AssociationChipsByIds";
import Flx from "../../../layout/Flx";
import { ASSOCIATION_CONFIG } from "../../config/associationConfig";
import Txt from "../../../typography/Txt";

/**
 * Display selected associations
 *
 * @param {Object} loan - The loan object from Redux store
 * @param {Object} associationIds - (Option 1) Direct object with association arrays
 * @param {Object} associations - (Option 2) Alternative prop name for associations
 * @param {Array} activeAssociationKeys - (Optional) Array of association type keys to display. If not provided, uses all keys from associations
 * @param {Boolean} disableDelete - If true, hides delete buttons
 * @param {Function} onDeleteAttachment - Callback when association is deleted
 * @param {Object} sx - Additional styling
 * @param {ReactNode} children - Additional content to render
 */
const SelectedAssociations = ({
  loan,
  associationIds,
  associations, // Alternative prop name
  activeAssociationKeys,
  disableDelete,
  emptyMessage = "No associations assigned",
  suppressEmptyMessage = false,
  onDeleteAttachment,
  sx,
  children,
}) => {
  // Use either associationIds or associations prop (prioritize associationIds for backwards compatibility)
  const resolvedAssociationIds = useMemo(() => {
    return associationIds || associations;
  }, [associationIds, associations]);

  // If activeAssociationKeys not provided, derive from the associations object
  const resolvedActiveKeys = useMemo(() => {
    if (activeAssociationKeys && Array.isArray(activeAssociationKeys)) {
      return activeAssociationKeys;
    }

    // Auto-derive keys from associations object
    if (resolvedAssociationIds && typeof resolvedAssociationIds === "object") {
      return Object.keys(resolvedAssociationIds).filter(
        (key) => !isEmpty(resolvedAssociationIds[key])
      );
    }

    return [];
  }, [activeAssociationKeys, resolvedAssociationIds]);

  // Early ret
  // lgurns for missing required data
  if (isNil(loan)) {
    console.warn("SelectedAssociations: loan is null or undefined");
    return null;
  }

  if (isNil(resolvedAssociationIds)) {
    return null;
  }

  if (resolvedActiveKeys.length === 0) {
    return null;
  }

  // Check if there are any associations to display
  const hasAttachments = resolvedActiveKeys.some(
    (key) => !isEmpty(resolvedAssociationIds?.[key])
  );

  if (!hasAttachments) {
    if (suppressEmptyMessage) {
      return null;
    }
    return (
      <Txt secondary sx={{ fontStyle: "italic", pt: 0.5 }}>
        {emptyMessage}
      </Txt>
    );
  }

  // console.log("associations", associations);
  return (
    <Flx wrap gap={1} sx={{ py: 0, ...sx }}>
      {children}
      {resolvedActiveKeys.map((key) => {
        const config = ASSOCIATION_CONFIG[key];
        const ids = resolvedAssociationIds?.[key];

        // Skip if no config, no ids, or ids is empty
        if (!config || isEmpty(ids)) {
          return null;
        }

        return (
          <AssociationChipsByIds
            key={key}
            loan={loan}
            ids={ids}
            config={config}
            disableDelete={disableDelete}
            onDelete={
              disableDelete ? null : (id) => onDeleteAttachment(id, key)
            }
          />
        );
      })}
    </Flx>
  );
};

export default SelectedAssociations;
